<section
  class="presentation-editor"
  [class.presentation-editor--behavior]="scope === 'behavior'"
  [class.presentation-editor--dashboard]="scope === 'dashboard'"
  [class.presentation-editor--detail]="scope === 'detail'"
  [attr.inert]="sourcePickerOpen || elementPickerOpen ? '' : null"
  aria-label="Step page settings"
>
  <header *ngIf="scope === 'detail' && !declaration">
    <h3>Detail workspace</h3>
    <button
      uiButton
      *ngIf="declaration && scope === 'detail'"
      type="button"
      (click)="openPreview()"
    >
      Preview page
    </button>
  </header>
  <p *ngIf="disabled" role="status">
    Finish the open step or path edits before changing these settings.
  </p>
  <p *ngIf="error" role="alert">{{ error }}</p>
  <section
    *ngIf="!editableRouting"
    class="setup-panel"
    aria-label="Enable page editing"
  >
    <h4>Enable routing and page editing</h4>
    <p>
      This older draft does not yet use its drawn paths to run work. Enable
      editable routing here to configure staff actions and pages. Existing paths
      stay in place; you will validate them before publication. Running
      workflows keep their original version.
    </p>
    <button
      uiButton
      *ngIf="canEnableRouting"
      type="button"
      [disabled]="disabled"
      (click)="enableRouting()"
    >
      Enable editable routing
    </button>
    <p *ngIf="!canEnableRouting">
      This draft format is not supported by this editor. Its saved settings are
      preserved.
    </p>
  </section>
  <phms-workflow-step-components-editor
    *ngIf="editableRouting && document && node"
    [document]="document"
    [node]="node"
    [active]="active"
    [scope]="scope"
    [immediateRoutingChanges]="immediateRoutingChanges"
    [externalWorkflowStatuses]="externalWorkflowStatuses"
    [workflowStatusesView]="workflowStatusesView"
    [latestDocument]="latestDocument"
    [ownerContext]="ownerContext"
    [formViewContext]="formViewContext"
    [resetVersion]="resetVersion"
    [hideDashboardPicker]="scope !== 'behavior'"
    [hideDetailEditor]="scope === 'detail'"
    [responsibilityEditor]="scope === 'behavior' ? responsibilityEditor : null"
    [responsibilitySummary]="responsibilitySummary"
    [beforeSelectionChange]="beforeSelectionChange"
    [focusResponsibility]="focusResponsibility"
    [focusAction]="focusAction"
    (pendingChangesChange)="onComponentPendingChanges($event)"
    (openForm)="openForm.emit($event)"
    (openAdvanced)="openAdvanced.emit()"
    [disabled]="disabled || loading"
    (documentChange)="applyComponentDocument($event)"
    (catalogChange)="onComponentCatalog($event)"
    (capabilitiesChange)="componentCapabilities = $event"
    (openActions)="openActions.emit($event)"
    (openOutcomes)="openOutcomes.emit($event)"
    (openPaths)="openPaths.emit()"
    (openOutcomePath)="openOutcomePath.emit($event)"
    (openDirectPath)="openDirectPath.emit()"
    (openRequirements)="openRequirements.emit()"
    (openWorkflowStatuses)="openWorkflowStatuses.emit($event)"
    (workflowStatusesOwnerRequested)="onWorkflowStatusesOwnerRequested($event)"
  />
  <ng-container *ngIf="scope === 'dashboard'">
    <ng-container *ngIf="dashboardSettingsState as dashboardState">
    <div class="dashboard-canvas-owner" [hidden]="dashboardTask !== 'canvas'" [attr.inert]="dashboardTask !== 'canvas' ? '' : null">
    <section
      *ngIf="!managedDashboardSettings && (!dashboard || !supportedDashboard) && queueSettingsOpen"
      class="dashboard-queue-empty"
      #dashboardInspector
      [attr.aria-label]="
        queueSettingsOpen ? 'Dashboard settings' : inspectorName + ' settings'
      "
    >
      <ui-panel panelTitle="Dashboard settings" [noGrow]="true">
        <div class="dashboard-panel-body">
          <ng-container *ngTemplateOutlet="queueSettings" />
        </div>
      </ui-panel>
    </section>
    <div *ngIf="!dashboard" class="dashboard-empty">
      <button
        uiButton
        severity="primary"
        [outlined]="false"
        type="button"
        class="primary"
        (click)="createDashboard()"
        [disabled]="dashboardControlsDisabled || (!managedDashboardSources && !defaultView)"
      >
        Create dashboard
      </button>
      <button
        *ngIf="!managedDashboardSources"
        uiButton
        type="button"
        (click)="showSources = !showSources"
        [disabled]="dashboardControlsDisabled || !supportedViews.length"
      >
        Choose another source
      </button>
    </div>
    <ng-container
      *ngIf="!dashboard && showSources"
      [ngTemplateOutlet]="sourcePicker"
    />
    <ng-container *ngIf="dashboard as view">
      <div class="dashboard-toolbar">
        <div class="dashboard-title">
          <h3 data-dashboard-canvas-heading tabindex="-1">
            <button
              *ngIf="
                dashboardMode === 'edit' && supportedDashboard;
                else dashboardName
              "
              class="dashboard-name-edit"
              type="button"
              [attr.aria-label]="
                'Rename dashboard: ' + (view.label || 'Untitled dashboard')
              "
              [disabled]="dashboardControlsDisabled"
              (click)="managedDashboardSettings ? renameManagedDashboard(view.id, dashboardState.origin) : editDashboardName()"
            >
              {{ view.label || "Untitled dashboard" }}
              <i class="pi pi-pencil" aria-hidden="true"></i>
            </button>
            <ng-template #dashboardName>{{
              view.label || "Untitled dashboard"
            }}</ng-template>
          </h3>
          <small>Sample data</small>
        </div>
        <label
          *ngIf="declaration!.dashboards!.length > 1"
          class="dashboard-scope"
          >Editing dashboard
          <select
            uiControl
            #dashboardPicker
            aria-label="Selected dashboard"
            [disabled]="queueChangesPending"
            [ngModel]="view.id"
            (ngModelChange)="
              selectDashboardFromControl(
                $event,
                dashboardPicker,
                managedDashboardSettings ? dashboardState.origin : undefined
              )
            "
          >
            <option
              *ngFor="let item of declaration!.dashboards; trackBy: trackId"
              [value]="item.id"
            >
              {{ item.label || item.id }}
            </option>
          </select>
        </label>
        <button
          uiButton
          *ngIf="dashboardMode === 'edit' && supportedDashboard"
          #addElementButton
          type="button"
          aria-label="Add dashboard element"
          [attr.aria-expanded]="managedDashboardElements ? elementPickerOpen : addToolsOpen"
          [disabled]="dashboardControlsDisabled"
          (click)="requestDashboardElements(view.id)"
        >
          <i class="pi pi-plus" aria-hidden="true"></i> Add element
        </button>
        <button
          uiButton
          type="button"
          *ngIf="dashboardMode === 'edit'"
          aria-label="Undo dashboard change"
          [disabled]="
            !canUndoDashboard ||
            dashboardControlsDisabled ||
            hasPendingChanges()
          "
          (click)="undoDashboard.emit()"
        >
          <i class="pi pi-undo" aria-hidden="true"></i> Undo
        </button>
        <label class="dashboard-canvas-size">
          <span class="dashboard-control-label">Canvas width</span>
          <select
            uiControl
            #canvasSizePicker
            aria-label="Dashboard canvas width"
            [ngModel]="canvasSize"
            (ngModelChange)="setCanvasSizeFromControl($event, canvasSizePicker)"
          >
            <option value="available">Available width</option>
            <option value="phone">Phone · 390px</option>
          </select>
        </label>
        <small class="dashboard-canvas-measurement">
          {{ canvasWidth ? canvasWidth + "px · " : "" }}100%
        </small>
        <button
          uiButton
          *ngIf="dashboardMode === 'edit'"
          class="dashboard-inspector-toggle"
          #dashboardInspectorToggle
          type="button"
          [attr.aria-expanded]="inspectorOpen"
          (click)="toggleInspector()"
        >
          {{ inspectorOpen ? "Hide settings" : "Show settings" }}
        </button>
        <button
          uiButton
          type="button"
          (click)="managedDashboardSettings ? openDashboardSettings(view.id, dashboardState.origin) : openQueueSettings()"
          data-dashboard-settings-entry
          #dashboardSettingsButton
          [disabled]="dashboardMode !== 'edit'"
          aria-label="Dashboard settings"
        >
          <i class="pi pi-cog" aria-hidden="true"></i> Settings
          <span *ngIf="queueSettingsIssues.length"
            >({{ queueSettingsIssues.length }})</span
          >
        </button>
        <button
          uiButton
          *ngIf="responsibilityEditor && !reviewWorkView"
          type="button"
          (click)="openResponsibilitySettings()"
          [disabled]="dashboardMode !== 'edit' || disabled || loading"
          aria-label="Assignment setup"
          [attr.title]="assignmentSetupSummary"
        >
          <i class="pi pi-user-edit" aria-hidden="true"></i> Assignment
        </button>
        <button
          uiButton
          type="button"
          [cdkMenuTriggerFor]="dashboardMenu"
          aria-label="Dashboard options"
          title="Dashboard options"
        >
          ⋯
        </button>
        <ng-template #dashboardMenu>
          <div
            cdkMenu
            class="dashboard-menu__content"
            aria-label="Dashboard options"
          >
            <span class="dashboard-source">{{
              installedView?.label || dashboardState.sourceLabel
            }}</span>
            <button
              *ngIf="managedDashboardSettings"
              uiButton
              cdkMenuItem
              type="button"
              data-manage-dashboards
              (cdkMenuItemTriggered)="openDashboardManagement(dashboardState.origin)"
              [disabled]="loading"
              [cdkMenuItemDisabled]="loading"
            >
              Manage dashboards
            </button>
            <button
              uiButton
              cdkMenuItem
              type="button"
              (cdkMenuItemTriggered)="openDashboardSources()"
              [cdkMenuItemDisabled]="
                dashboardControlsDisabled || (!managedDashboardSources && !supportedViews.length)
              "
              [disabled]="dashboardControlsDisabled || (!managedDashboardSources && !supportedViews.length)"
              data-add-dashboard
            >
              Add another dashboard
            </button>
            <button
              uiButton
              cdkMenuItem
              severity="danger"
              type="button"
              class="danger"
              data-remove-dashboard
              (cdkMenuItemTriggered)="removeDashboard(view.id)"
              [cdkMenuItemDisabled]="dashboardControlsDisabled"
              [disabled]="dashboardControlsDisabled"
            >
              Remove dashboard
            </button>
          </div>
        </ng-template>
      </div>
      <ng-container *ngIf="dashboardPreviewState as previewState">
      <ui-tab-group
        class="dashboard-mode-workspace"
        [stretchTabs]="false"
        [navigationTabs]="modeTabs"
        [activeTabId]="dashboardMode"
        navigationAriaLabel="Dashboard mode"
        (tabRequested)="
          requestDashboardMode(
            $event,
            managedDashboardSettings ? previewState.origin : undefined
          )
        "
      >
        <section
          *ngIf="dashboardMode === 'try'"
          class="dashboard-preview-controls"
          data-dashboard-preview-state
          aria-label="Dashboard preview controls"
        >
          <p class="dashboard-preview-hint">
            Reset restores this dashboard's authored starting filters.
          </p>
          <button
            uiButton
            type="button"
            data-reset-dashboard-preview
            [disabled]="loading || !previewState.available"
            (click)="resetDashboardPreview(previewState.origin)"
          >
            <i class="pi pi-refresh" aria-hidden="true"></i> Reset preview
          </button>
          <p *ngIf="previewState.reason" class="dashboard-preview-reason" role="status">
            {{ previewState.reason }}
          </p>
        </section>
        <ng-container *ngIf="showSources" [ngTemplateOutlet]="sourcePicker" />
        <small
          *ngIf="supportedDashboard && !view.label.trim()"
          class="dashboard-notice"
          role="alert"
          >Enter a dashboard label.</small
        >
        <p *ngIf="!supportedDashboard" class="dashboard-notice" role="status">
          This saved dashboard uses an unsupported view. Its settings are
          preserved.
        </p>
        <ng-container *ngIf="supportedDashboard && presentation as layout">
          <ng-template #summaryTools>
            <button uiButton type="button" *ngIf="managedDashboardElements" [disabled]="dashboardControlsDisabled"
              (click)="openDashboardElements(view.id, 'tile')">Add summary tile…</button>
            <select
              *ngIf="!managedDashboardElements"
              uiControl
              aria-label="Add summary tile"
              class="dashboard-context-add"
              #tilePicker
              (change)="addTileChoice(tilePicker.value); tilePicker.value = ''"
              [disabled]="
                dashboardControlsDisabled || layout.tiles.length >= 12
              "
            >
              <option value="" disabled selected>Add summary tile…</option>
              <option value="*">Total records</option>
              <optgroup label="Step progress" *ngIf="standardStepProgress">
                <option
                  *ngFor="let status of stepProgressChoices; trackBy: trackCode"
                  [value]="'stepProgress:' + status.code"
                >
                  {{ status.label }} count
                </option>
              </optgroup>
              <optgroup
                *ngIf="
                  !standardStepProgress ||
                  installedView?.capability?.key?.code ===
                    'workflow.review.records'
                "
                [label]="
                  installedView?.capability?.key?.code ===
                  'workflow.review.records'
                    ? 'Review state'
                    : 'Step progress'
                "
              >
                <option
                  *ngFor="let status of queueStatuses; trackBy: trackCode"
                  [value]="status.code"
                >
                  {{ status.label }} count
                </option>
              </optgroup>
              <optgroup *ngIf="assignmentSupported" label="Assignment">
                <option
                  *ngFor="let choice of assignmentChoices; trackBy: trackCode"
                  [value]="'assignment:' + choice.code"
                >
                  {{ choice.label }} count
                </option>
              </optgroup>
              <optgroup *ngIf="workflowStatuses.length" label="Workflow status">
                <option
                  *ngFor="let status of workflowStatuses; trackBy: trackCode"
                  [value]="'workflow:' + status.code"
                >
                  {{ status.label }} count
                </option>
              </optgroup>
              <optgroup *ngIf="stepStatuses.length" label="Step status">
                <option
                  *ngFor="let status of stepStatuses; trackBy: trackCode"
                  [value]="'step:' + status.code"
                >
                  {{ status.label }} count
                </option>
              </optgroup>
            </select>
          </ng-template>
          <ng-template #filterTools>
            <button uiButton type="button" *ngIf="managedDashboardElements" [disabled]="dashboardControlsDisabled"
              (click)="openDashboardElements(view.id, 'filter')">Add staff filter…</button>
            <select
              *ngIf="!managedDashboardElements"
              uiControl
              aria-label="Add filter"
              class="dashboard-context-add"
              #filterPicker
              (change)="addFilter(filterPicker.value); filterPicker.value = ''"
              [disabled]="dashboardControlsDisabled || !availableFilters.length"
            >
              <option value="" disabled selected>Add staff filter…</option>
              <option
                *ngFor="let filter of availableFilters; trackBy: trackField"
                [value]="filter.field"
              >
                {{ filter.label }}
              </option>
            </select>
          </ng-template>
          <ng-template #columnTools>
            <button uiButton type="button" *ngIf="managedDashboardElements" [disabled]="dashboardControlsDisabled"
              (click)="openDashboardElements(view.id, 'column')">Add table column…</button>
            <select
              *ngIf="!managedDashboardElements"
              uiControl
              aria-label="Add column"
              class="dashboard-context-add"
              #columnPicker
              (change)="addColumn(columnPicker.value); columnPicker.value = ''"
              [disabled]="dashboardControlsDisabled || !availableColumns.length"
            >
              <option value="" disabled selected>Add table column…</option>
              <option
                *ngFor="let column of availableColumns; trackBy: trackField"
                [value]="column.field"
              >
                {{ column.label }}
              </option>
            </select>
          </ng-template>
          <section
            *ngIf="dashboardMode === 'edit' && addToolsOpen && !managedDashboardElements"
            class="dashboard-add"
            aria-label="Add dashboard element"
          >
            <ng-container *ngTemplateOutlet="summaryTools" />
            <ng-container *ngTemplateOutlet="filterTools" />
            <ng-container *ngTemplateOutlet="columnTools" />
            <button
              uiButton
              type="button"
              aria-label="Close add dashboard element"
              (click)="closeAddTools()"
            >
              Close
            </button>
          </section>
          <ui-tab-group
            class="dashboard-mobile-panels"
            #dashboardPanels
            [navigationTabs]="
              dashboardMode === 'edit' && inspectorOpen ? mobileTabs : []
            "
            [activeTabId]="mobilePanel"
            navigationAriaLabel="Dashboard editing pane"
            (tabRequested)="requestMobilePanel($event)"
          >
            <div
              class="dashboard-workspace"
              [class.dashboard-workspace--editing]="
                dashboardMode === 'edit' && inspectorOpen
              "
              [class.dashboard-workspace--queue]="
                dashboardMode === 'edit' && inspectorOpen && queueSettingsOpen
              "
              [class.dashboard-workspace--settings]="mobilePanel === 'settings'"
            >
              <div class="dashboard-canvas">
                <phms-workflow-presentation-preview
                  *ngIf="document && node"
                  [document]="document"
                  [node]="node"
                  scope="dashboard"
                  [dashboardId]="view.id"
                  [dashboardResetVersion]="dashboardPreviewResetVersion"
                  [componentCatalog]="componentCatalog"
                  [compact]="false"
                  [canvasSize]="canvasSize"
                  (canvasWidthChange)="canvasWidth = $event"
                  [authoring]="dashboardMode === 'edit'"
                  [dashboardEditingDisabled]="dashboardControlsDisabled"
                  [dashboardSummaryTools]="
                    dashboardMode === 'edit' ? summaryTools : null
                  "
                  [dashboardFilterTools]="
                    dashboardMode === 'edit' ? filterTools : null
                  "
                  [dashboardColumnTools]="
                    dashboardMode === 'edit' ? columnTools : null
                  "
                  [selectedTarget]="selection"
                  (configure)="configure($event)"
                  (columnOrderChange)="setColumnOrder($event)"
                />
              </div>
              <aside
                *ngIf="dashboardMode === 'edit' && inspectorOpen"
                class="dashboard-inspector"
                #dashboardInspector
                [attr.aria-label]="
                  queueSettingsOpen
                    ? 'Dashboard settings'
                    : inspectorName + ' settings'
                "
              >
                <ui-panel
                  [headerTemplate]="inspectorHeader"
                  [noGrow]="true"
                  [flexGap]="'none'"
                >
                  <div class="dashboard-panel-body">
                    <ng-container
                      *ngIf="queueSettingsOpen && !managedDashboardSettings; else componentSettings"
                      [ngTemplateOutlet]="queueSettings"
                    />
                    <ng-template #componentSettings>
                      <label class="dashboard-component-picker"
                        >Choose what to edit
                        <select
                          uiControl
                          #componentPicker
                          aria-label="Select dashboard component"
                          [ngModel]="
                            selection ? selection.kind + ':' + selection.id : ''
                          "
                          (ngModelChange)="
                            selectItemFromControl($event, componentPicker)
                          "
                          [disabled]="loading || queueChangesPending"
                        >
                          <option value="">Select component</option>
                          <optgroup label="Summary tiles">
                            <option
                              *ngFor="
                                let tile of layout.tiles;
                                trackBy: trackId
                              "
                              [value]="'tile:' + tile.id"
                            >
                              {{ tile.label || "Untitled tile" }}
                            </option>
                          </optgroup>
                          <optgroup label="Staff filters">
                            <option
                              *ngFor="
                                let filter of layout.filters;
                                trackBy: trackField
                              "
                              [value]="'filter:' + filter.field"
                            >
                              {{ filter.label || "Untitled filter" }}
                            </option>
                          </optgroup>
                          <optgroup label="Table columns">
                            <option
                              *ngFor="
                                let column of layout.columns;
                                trackBy: trackField
                              "
                              [value]="'column:' + column.field"
                            >
                              {{ column.label || "Untitled column" }}
                            </option>
                          </optgroup>
                        </select>
                      </label>
                      <ng-container
                        *ngIf="selectedItem as item; else noSelection"
                      >
                        <p class="dashboard-setting-hint">
                          {{ selectedKindLabel }} settings
                        </p>
                        <label
                          >Display label<input
                            uiControl
                            aria-label="Component label"
                            maxlength="120"
                            data-dashboard-rename="item"
                            [attr.aria-invalid]="!!selectedItemLabelError"
                            [attr.aria-describedby]="
                              selectedItemLabelError
                                ? 'dashboard-component-name-error'
                                : null
                            "
                            [ngModel]="selectedItemLabelValue"
                            (ngModelChange)="updateItemLabel($event)"
                            (blur)="commitDashboardRename()"
                            (keydown)="onDashboardRenameKeydown($event)"
                            [disabled]="dashboardControlsDisabled"
                        /></label>
                        <small
                          *ngIf="selectedItemLabelError"
                          id="dashboard-component-name-error"
                          role="alert"
                          >{{ selectedItemLabelError }}</small
                        >
                        <ng-container *ngIf="selectedTile as tile">
                          <label
                            >Count by
                            <select
                              uiControl
                              aria-label="Tile status type"
                              [ngModel]="tile.statusScope || 'progress'"
                              (ngModelChange)="setTileScope($event)"
                              [disabled]="dashboardControlsDisabled"
                            >
                              <option value="progress">
                                {{
                                  installedView?.capability?.key?.code ===
                                  "workflow.review.records"
                                    ? "Review state"
                                    : "Step progress"
                                }}
                              </option>
                              <option
                                value="stepProgress"
                                *ngIf="standardStepProgress"
                              >
                                Step progress (standard)
                              </option>
                              <option
                                *ngIf="
                                  assignmentSupported ||
                                  tile.statusScope === 'assignment'
                                "
                                value="assignment"
                              >
                                Assignment
                              </option>
                              <option
                                *ngIf="
                                  workflowStatuses.length ||
                                  tile.statusScope === 'workflow'
                                "
                                value="workflow"
                              >
                                Workflow status
                              </option>
                              <option
                                *ngIf="
                                  stepStatuses.length ||
                                  tile.statusScope === 'step'
                                "
                                value="step"
                              >
                                Step label
                              </option>
                            </select>
                          </label>
                          <label
                            >Count
                            <select
                              uiControl
                              aria-label="Tile count"
                              [ngModel]="tile.status || ''"
                              (ngModelChange)="
                                setTile({ status: $event || null })
                              "
                              [disabled]="dashboardControlsDisabled"
                            >
                              <option value="">Total records</option>
                              <option
                                *ngFor="
                                  let status of tileStatuses;
                                  trackBy: trackCode
                                "
                                [value]="status.code"
                              >
                                {{ status.label }}
                              </option>
                              <option
                                *ngIf="unrecognizedTileStatus"
                                [value]="unrecognizedTileStatus"
                              >
                                {{ unrecognizedTileStatus }} (saved,
                                unavailable)
                              </option>
                            </select>
                          </label>
                          <label
                            >Tone<select
                              uiControl
                              aria-label="Tile tone"
                              [ngModel]="tile.tone"
                              (ngModelChange)="setTile({ tone: $event })"
                              [disabled]="dashboardControlsDisabled"
                            >
                              <option *ngFor="let tone of tones" [value]="tone">
                                {{ tone | titlecase }}
                              </option>
                            </select></label
                          >
                          <label class="check"
                            ><input
                              uiControl
                              type="checkbox"
                              [ngModel]="tile.filterOnClick"
                              (ngModelChange)="
                                setTile({ filterOnClick: $event })
                              "
                              [disabled]="dashboardControlsDisabled"
                            />Filter when selected</label
                          >
                        </ng-container>
                        <ng-container *ngIf="selectedFilter as filter">
                          <label *ngIf="filter.field === 'search'"
                            >Starting value<input
                              uiControl
                              aria-label="Filter initial value"
                              maxlength="100"
                              [ngModel]="filter.initialValue"
                              (ngModelChange)="
                                setFilter({ initialValue: $event })
                              "
                              [disabled]="dashboardControlsDisabled"
                          /></label>
                          <ng-container *ngIf="filter.field !== 'search'">
                            <label
                              >Starting value<select
                                uiControl
                                aria-label="Filter initial value"
                                [ngModel]="filter.initialValue"
                                (ngModelChange)="
                                  setFilter({ initialValue: $event })
                                "
                                [disabled]="dashboardControlsDisabled"
                              >
                                <option
                                  *ngFor="
                                    let choice of initialFilterChoices;
                                    trackBy: trackCode
                                  "
                                  [value]="choice.code"
                                >
                                  {{ choice.label }}
                                </option>
                              </select></label
                            >
                            <fieldset class="filter-choices">
                              <legend>Choices offered to staff</legend>
                              <label
                                class="check"
                                *ngFor="
                                  let choice of filterChoices;
                                  trackBy: trackCode
                                "
                                ><input
                                  uiControl
                                  type="checkbox"
                                  [checked]="
                                    filterAllowedValues.includes(choice.code)
                                  "
                                  (change)="
                                    toggleFilterChoice(
                                      choice.code,
                                      $any($event.target).checked
                                    )
                                  "
                                  [disabled]="
                                    dashboardControlsDisabled ||
                                    (filterAllowedValues.length === 1 &&
                                      filterAllowedValues.includes(choice.code))
                                  "
                                />{{ choice.label }}</label
                              >
                            </fieldset>
                          </ng-container>
                          <p class="dashboard-setting-hint">
                            Staff can change or clear this filter. It only
                            searches work included in this dashboard.
                          </p>
                          <p
                            *ngIf="
                              filter.field === 'status' &&
                              dashboard?.defaults?.['status']
                            "
                            class="dashboard-setting-hint"
                          >
                            Included status: {{ includedStatusLabel }}. Clearing
                            this filter still keeps that restriction.
                          </p>
                        </ng-container>
                        <div class="inspector-actions">
                          <button
                            uiButton
                            type="button"
                            [attr.aria-label]="
                              selection!.kind === 'column'
                                ? 'Move column left'
                                : 'Move ' + selection!.kind + ' earlier'
                            "
                            [title]="
                              selection!.kind === 'column'
                                ? 'Move column left'
                                : 'Move ' + selection!.kind + ' earlier'
                            "
                            (click)="moveSelected(-1)"
                            [disabled]="
                              dashboardControlsDisabled || selectedIndex <= 0
                            "
                          >
                            <span aria-hidden="true">{{
                              selection!.kind === "column" ? "←" : "↑"
                            }}</span>
                          </button>
                          <button
                            uiButton
                            type="button"
                            [attr.aria-label]="
                              selection!.kind === 'column'
                                ? 'Move column right'
                                : 'Move ' + selection!.kind + ' later'
                            "
                            [title]="
                              selection!.kind === 'column'
                                ? 'Move column right'
                                : 'Move ' + selection!.kind + ' later'
                            "
                            (click)="moveSelected(1)"
                            [disabled]="
                              dashboardControlsDisabled ||
                              selectedIndex >= selectedCount - 1
                            "
                          >
                            <span aria-hidden="true">{{
                              selection!.kind === "column" ? "→" : "↓"
                            }}</span>
                          </button>
                          <button
                            uiButton
                            severity="danger"
                            type="button"
                            (click)="removeSelected()"
                            [disabled]="
                              dashboardControlsDisabled || requiredColumn
                            "
                          >
                            Remove
                          </button>
                        </div>
                        <small *ngIf="requiredColumn">Required column</small>
                      </ng-container>
                      <ng-template #noSelection
                        ><span class="selection-hint"
                          >Select a summary tile, staff filter or table column
                          on the page to edit it.</span
                        ></ng-template
                      >
                    </ng-template>
                  </div>
                </ui-panel>
              </aside>
            </div>
          </ui-tab-group>
        </ng-container>
      </ui-tab-group>
      </ng-container>
    </ng-container>
    <ng-template #sourcePicker>
      <div class="dashboard-source-picker" *ngIf="!managedDashboardSources">
        <label
          >Dashboard source<select
            uiControl
            aria-label="Dashboard source"
            [(ngModel)]="additionalSource"
            [disabled]="dashboardControlsDisabled"
          >
            <option value="">Choose a source</option>
            <option
              *ngFor="let source of supportedViews; trackBy: trackView"
              [value]="viewIdentity(source)"
            >
              {{ source.label }}
            </option>
          </select></label
        >
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          type="button"
          (click)="createDashboard(additionalSource)"
          [disabled]="dashboardControlsDisabled || !additionalSource"
        >
          Add dashboard
        </button>
        <button uiButton type="button" (click)="showSources = false">
          Cancel
        </button>
      </div>
    </ng-template>
    </div>
    <section
      *ngIf="managedDashboardSettings && dashboardTask === 'settings'"
      class="dashboard-task"
      data-dashboard-settings-task
      aria-labelledby="dashboard-settings-task-title"
    >
      <header class="dashboard-task-header">
        <button
          uiButton
          [text]="true"
          type="button"
          data-back-dashboard-canvas
          [disabled]="loading"
          (click)="backToDashboardCanvas(dashboardState.origin)"
        >
          <i class="pi pi-arrow-left" aria-hidden="true"></i> Back to dashboard
          canvas
        </button>
        <h3
          id="dashboard-settings-task-title"
          data-dashboard-task-heading
          tabindex="-1"
        >
          Dashboard settings
        </h3>
        <span>{{ dashboardState.label }}</span>
      </header>
      <div class="dashboard-task-body">
        <p
          *ngIf="dashboardState.unavailableReason"
          role="status"
          data-dashboard-settings-unavailable
        >
          {{ dashboardState.unavailableReason }}
        </p>
        <ng-container
          *ngIf="!dashboardState.unavailableReason"
          [ngTemplateOutlet]="queueSettings"
          [ngTemplateOutletContext]="{
            managed: true,
            origin: dashboardState.origin,
            view: dashboardState.dashboard,
            settings: dashboardState,
          }"
        />
        <details class="dashboard-task-technical" data-dashboard-technical>
          <summary>Technical details</summary>
          <pre>{{ dashboardState.technical | json }}</pre>
        </details>
      </div>
    </section>
    <section
      *ngIf="managedDashboardSettings && dashboardTask === 'manage'"
      class="dashboard-task"
      data-dashboard-management-task
      aria-labelledby="dashboard-management-task-title"
    >
      <header class="dashboard-task-header">
        <button
          uiButton
          [text]="true"
          type="button"
          data-back-dashboard-canvas
          [disabled]="loading"
          (click)="backToDashboardCanvas(dashboardState.origin)"
        >
          <i class="pi pi-arrow-left" aria-hidden="true"></i> Back to dashboard
          canvas
        </button>
        <h3
          id="dashboard-management-task-title"
          data-dashboard-task-heading
          tabindex="-1"
        >
          Manage dashboards
        </h3>
        <button
          uiButton
          type="button"
          data-managed-add-dashboard
          [disabled]="dashboardControlsDisabled"
          (click)="addManagedDashboard(dashboardState.origin)"
        >
          <i class="pi pi-plus" aria-hidden="true"></i> Add dashboard
        </button>
      </header>
      <div class="dashboard-task-body">
        <p>
          {{ dashboardState.rows.length }} dashboards on
          {{ node?.displayName || "this step" }}.
        </p>
        <p *ngIf="dashboardState.unavailableReason" role="status">
          {{ dashboardState.unavailableReason }}
        </p>
        <table
          *ngIf="dashboardState.rows.length"
          class="dashboard-management-table"
          aria-label="Dashboards on this step"
        >
          <thead>
            <tr>
              <th scope="col">Dashboard</th>
              <th scope="col">Source</th>
              <th scope="col">Starting view</th>
              <th scope="col">Audience</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (row of dashboardState.rows; track row.id + ":" + $index) {
              <tr [attr.data-dashboard-row]="row.id">
                <td>
                  <div class="dashboard-management-name">
                    <strong>{{ row.label }}</strong
                    ><span *ngIf="row.current" class="dashboard-management-current"
                      >Current</span
                    >
                  </div>
                  <p
                    *ngIf="row.unavailableReason"
                    class="dashboard-management-unavailable"
                    role="status"
                  >
                    {{ row.unavailableReason }}
                  </p>
                </td>
                <td>{{ row.sourceLabel }}</td>
                <td>{{ row.startingView }}</td>
                <td>{{ row.audienceLabel }}</td>
                <td>
                  <div class="dashboard-management-actions">
                    <button
                      uiButton
                      type="button"
                      [attr.aria-label]="
                        'Open dashboard ' + row.label + ' (' + row.id + ')'
                      "
                      [disabled]="loading || !row.id"
                      (click)="openManagedDashboard(row.id, dashboardState.origin)"
                    >
                      Open
                    </button>
                    <button
                      uiButton
                      [text]="true"
                      type="button"
                      [attr.aria-label]="
                        'Rename dashboard ' + row.label + ' (' + row.id + ')'
                      "
                      [disabled]="
                        dashboardControlsDisabled || !!row.unavailableReason
                      "
                      (click)="
                        renameManagedDashboard(row.id, dashboardState.origin)
                      "
                    >
                      Rename
                    </button>
                    <button
                      uiButton
                      [text]="true"
                      severity="danger"
                      type="button"
                      [attr.aria-label]="
                        'Remove dashboard ' + row.label + ' (' + row.id + ')'
                      "
                      [disabled]="dashboardControlsDisabled || !row.id"
                      (click)="
                        removeManagedDashboard(row.id, dashboardState.origin)
                      "
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
        <p *ngIf="!dashboardState.rows.length">
          No dashboards are configured. Add a dashboard to choose an installed
          source.
        </p>
        <p>You can use the same source for more than one dashboard.</p>
        <details
          class="dashboard-task-technical"
          data-dashboard-management-technical
        >
          <summary>Technical details</summary>
          <pre>{{ dashboardState.technical | json }}</pre>
        </details>
      </div>
    </section>
    </ng-container>
  </ng-container>
  <ng-template #inspectorHeader>
    <header class="dashboard-panel-heading">
      <strong data-dashboard-heading tabindex="-1">{{ inspectorName }}</strong>
      <button
        uiButton
        [text]="true"
        [outlined]="false"
        type="button"
        [attr.aria-label]="
          queueSettingsOpen
            ? 'Close dashboard settings'
            : 'Close component settings'
        "
        (click)="closeInspector()"
      >
        ×
      </button>
    </header>
  </ng-template>
  <ng-template
    #queueSettings
    let-managed="managed"
    let-origin="origin"
    let-taskView="view"
    let-settings="settings"
  >
    <ng-container *ngIf="managed ? taskView : dashboard as view">
      <div
        [class.dashboard-task-settings]="managed"
        [class.dashboard-legacy-settings]="!managed"
      >
        <section class="dashboard-setting-section dashboard-settings-general">
          <h4 *ngIf="managed">General</h4>
          <label class="dashboard-label"
            >Dashboard name
            <input
              uiControl
              aria-label="Dashboard label"
              data-dashboard-rename="dashboard"
              [attr.aria-invalid]="!!dashboardLabelError"
              [attr.aria-describedby]="
                dashboardLabelError ? 'dashboard-name-error' : null
              "
              #dashboardNameModel="ngModel"
              [ngModel]="dashboardLabelValue"
              (ngModelChange)="
                updateDashboardName($event, origin, dashboardNameModel)
              "
              (blur)="commitDashboardName(origin)"
              (keydown)="dashboardNameKeydown($event, origin)"
              maxlength="120"
              [disabled]="dashboardControlsDisabled || !supportedDashboard"
            />
          </label>
          <small
            *ngIf="dashboardLabelError"
            id="dashboard-name-error"
            role="alert"
            >{{ dashboardLabelError }}</small
          >
          <dl *ngIf="managed" class="dashboard-task-facts">
            <div>
              <dt>Source</dt>
              <dd>{{ settings.sourceLabel }}</dd>
            </div>
            <div>
              <dt>Selected step</dt>
              <dd>{{ node?.displayName }}</dd>
            </div>
          </dl>
        </section>
        <ng-container *ngIf="!managed" [ngTemplateOutlet]="assignmentSettings" />
        <section
          class="dashboard-setting-section dashboard-settings-included"
          [attr.aria-label]="managed ? 'Included work' : 'Which work appears'"
        >
          <h4>{{ managed ? "Included work" : "Which work appears" }}</h4>
          <ng-container *ngIf="hearingDayView">
            <label
              >Calendar time zone
              <input
                uiControl
                aria-label="Hearing-day time zone"
                #dashboardTimeZoneModel="ngModel"
                [ngModel]="view.defaults['timeZoneId'] || ''"
                (ngModelChange)="
                  setDashboardSetting(
                    'timeZoneId',
                    $event,
                    origin,
                    dashboardTimeZoneModel
                  )
                "
                maxlength="120"
                [disabled]="dashboardControlsDisabled"
              />
            </label>
            <label
              >Default hearing day
              <input
                uiControl
                type="date"
                aria-label="Default hearing day"
                #dashboardDayModel="ngModel"
                [ngModel]="view.defaults['day'] || ''"
                (ngModelChange)="
                  setDashboardSetting('day', $event, origin, dashboardDayModel)
                "
                [disabled]="dashboardControlsDisabled"
              />
            </label>
            <p class="dashboard-setting-hint">
              Leave the day blank to open today in the selected time zone. Staff
              can select another day or posture; calendar matches grant no hearing
              action.
            </p>
          </ng-container>
          <p class="dashboard-setting-hint">
            {{
              reviewWorkView
                ? "Show the latest pending or returned review recorded at this step, with its actual current work."
                : "Show work at " +
                  (node?.displayName || "this step") +
                  ". Limit it to the statuses this dashboard is for."
            }}
          </p>
          <fieldset
            class="business-status-scope"
            *ngIf="workflowStatuses.length || view.workflowStatusCodes != null"
            [disabled]="dashboardControlsDisabled || !supportedDashboard"
          >
            <legend>Workflow statuses</legend>
            <small *ngIf="includedStatusCodes('workflow') == null"
              >All workflow statuses included. Select statuses to limit this
              dashboard.</small
            >
            <label
              class="check"
              *ngFor="let status of workflowStatuses; trackBy: trackCode"
              ><input
                uiControl
                type="checkbox"
                [attr.aria-label]="'Include workflow status ' + status.label"
                [checked]="
                  includedStatusCodes('workflow')?.includes(status.code) || false
                "
                (change)="
                  toggleDashboardSetting(
                    'workflow',
                    status.code,
                    $any($event.target).checked,
                    origin,
                    $any($event.target)
                  )
                "
              />{{ status.label }}</label
            >
            <button
              *ngIf="includedStatusCodes('workflow') != null"
              uiButton
              type="button"
              (click)="setDashboardSetting('all-workflow', '', origin)"
            >
              Show all workflow statuses
            </button>
          </fieldset>
          <fieldset
            class="business-status-scope"
            *ngIf="stepStatuses.length || view.stepStatusCodes != null"
            [disabled]="dashboardControlsDisabled || !supportedDashboard"
          >
            <legend>Step labels</legend>
            <small *ngIf="includedStatusCodes('step') == null"
              >All step labels included. Select labels to limit this
              dashboard.</small
            >
            <label
              class="check"
              *ngFor="let status of stepStatuses; trackBy: trackCode"
              ><input
                uiControl
                type="checkbox"
                [attr.aria-label]="'Include step status ' + status.label"
                [checked]="
                  includedStatusCodes('step')?.includes(status.code) || false
                "
                (change)="
                  toggleDashboardSetting(
                    'step',
                    status.code,
                    $any($event.target).checked,
                    origin,
                    $any($event.target)
                  )
                "
              />{{ status.label }}</label
            >
            <button
              *ngIf="includedStatusCodes('step') != null"
              uiButton
              type="button"
              (click)="setDashboardSetting('all-step', '', origin)"
            >
              Show all step labels
            </button>
          </fieldset>
          <fieldset *ngIf="reviewWorkView" class="business-status-scope">
            <legend>Included review states</legend>
            <label
              *ngFor="let status of statuses; trackBy: trackCode"
              class="check"
            >
              <input
                type="checkbox"
                [attr.aria-label]="'Include review state ' + status.label"
                [checked]="reviewWorkStatuses.includes(status.code)"
                [disabled]="dashboardControlsDisabled || !supportedDashboard"
                (change)="
                  toggleDashboardSetting(
                    'review',
                    status.code,
                    $any($event.target).checked,
                    origin,
                    $any($event.target)
                  )
                "
              />{{ status.label }}
            </label>
            <small
              >Returned reviews remain separate from the current correction visit.
              Current-work links are checked by the server. Removing a state
              removes its summary tiles and resets unsupported staff
              filters.</small
            >
          </fieldset>
          <label *ngIf="!reviewWorkView"
            >{{
              installedView?.capability?.key?.code === "workflow.review.records"
                ? "Review state"
                : "Step progress"
            }}
            <select
              uiControl
              aria-label="Status included"
              #dashboardStatusModel="ngModel"
              [ngModel]="view.defaults['status'] || ''"
              (ngModelChange)="
                setDashboardSetting(
                  'status',
                  $event,
                  origin,
                  dashboardStatusModel
                )
              "
              [disabled]="dashboardControlsDisabled || !supportedDashboard"
            >
              <option value="">All states</option>
              <option
                *ngFor="let status of statuses; trackBy: trackCode"
                [value]="status.code"
              >
                {{ status.label }}
              </option>
              <option *ngIf="unrecognizedStatus" [value]="unrecognizedStatus">
                {{ unrecognizedStatus }} (saved)
              </option>
            </select>
          </label>
          <p class="dashboard-setting-hint">
            These settings limit the work on this dashboard. Staff filters cannot
            show work outside this scope.
          </p>
          <details
            class="dashboard-advanced-scope"
            *ngIf="view.queueScope === 'receiving-queue'"
            [open]="view.queueScope === 'receiving-queue'"
          >
            <summary>
              Advanced work filter<span
                *ngIf="view.queueScope === 'receiving-queue'"
              >
                · Team queue restriction saved</span
              >
            </summary>
            <label>Earlier team queue filter</label>
            <p class="dashboard-setting-hint">
              This draft has a saved restriction to its earlier receiving queue.
              Switch to role assignment to remove queue restrictions from all this
              step's dashboards, or show all step work here.
            </p>
            <small *ngIf="view.queueScope === 'receiving-queue'"
              >Team queue:
              {{
                receivingQueue ? roleLabel(receivingQueue) : "Not configured"
              }}</small
            >
            <button
              uiButton
              type="button"
              (click)="
                managed
                  ? manageDashboardInWork(origin)
                  : openResponsibilitySettings()
              "
            >
              Review assignment roles
            </button>
            <button
              uiButton
              type="button"
              [disabled]="dashboardControlsDisabled"
              (click)="setDashboardSetting('queue', '', origin)"
            >
              Show all step work
            </button>
          </details>
        </section>
        <section
          class="dashboard-setting-section dashboard-settings-order"
          aria-label="Work order"
          *ngIf="managed || workflowStatuses.length || priorityStatuses.length"
        >
          <h4>{{ managed ? "Work order" : "Work priority" }}</h4>
          <p
            class="dashboard-setting-hint"
            *ngIf="workflowStatuses.length || priorityStatuses.length"
          >
            Optionally show selected workflow statuses first. Within each
            priority, and for all remaining work, show the most recently updated
            first.
          </p>
          <ol class="dashboard-priority-list" *ngIf="priorityStatuses.length">
            <li
              *ngFor="
                let status of priorityStatuses;
                let index = index;
                trackBy: trackCode
              "
            >
              <span>{{ status.label }}</span>
              <button
                uiButton
                type="button"
                [attr.aria-label]="'Raise priority for ' + status.label"
                (click)="moveDashboardPriority(index, -1, origin)"
                [disabled]="dashboardControlsDisabled || index === 0"
              >
                ↑
              </button>
              <button
                uiButton
                type="button"
                [attr.aria-label]="'Lower priority for ' + status.label"
                (click)="moveDashboardPriority(index, 1, origin)"
                [disabled]="
                  dashboardControlsDisabled ||
                  index === priorityStatuses.length - 1
                "
              >
                ↓
              </button>
              <button
                uiButton
                type="button"
                [attr.aria-label]="'Remove priority for ' + status.label"
                (click)="
                  setDashboardSetting('priority-remove', status.code, origin)
                "
                [disabled]="dashboardControlsDisabled"
              >
                Remove
              </button>
            </li>
          </ol>
          <label
            *ngIf="!managed || workflowStatuses.length || priorityStatuses.length"
            >Prioritize a workflow status
            <select
              uiControl
              aria-label="Add priority workflow status"
              #priorityAddModel="ngModel"
              [ngModel]="''"
              (ngModelChange)="
                setDashboardSetting(
                  'priority-add',
                  $event,
                  origin,
                  priorityAddModel
                )
              "
              [disabled]="
                dashboardControlsDisabled || !availablePriorityStatuses.length
              "
            >
              <option value="" selected disabled>
                Choose a workflow status…
              </option>
              <option
                *ngFor="
                  let status of availablePriorityStatuses;
                  trackBy: trackCode
                "
                [value]="status.code"
              >
                {{ status.label }}
              </option>
            </select>
          </label>
          <small *ngIf="!priorityStatuses.length"
            >Most recently updated first.</small
          >
        </section>
        <fieldset
          class="dashboard-audience dashboard-settings-audience"
          [disabled]="dashboardControlsDisabled"
        >
          <legend>Who can see this dashboard</legend>
          <label
            >Who can view<select
              uiControl
              aria-label="Dashboard audience"
              #dashboardAudienceModel="ngModel"
              [ngModel]="audienceMode"
              (ngModelChange)="
                setDashboardSetting(
                  'audience',
                  $event,
                  origin,
                  dashboardAudienceModel
                )
              "
            >
              <option value="all">Everyone with record access</option>
              <option value="roles">Selected roles</option>
            </select></label
          >
          <div
            *ngIf="audienceMode === 'roles'"
            class="dashboard-role-list"
            aria-label="Dashboard roles"
          >
            <label *ngFor="let code of visibleAudienceRoles" class="check"
              ><input
                uiControl
                type="checkbox"
                [attr.aria-label]="roleLabel(code)"
                [checked]="view.audienceRoleCodes?.includes(code)"
                (change)="
                  toggleDashboardSetting(
                    'role',
                    code,
                    $any($event.target).checked,
                    origin,
                    $any($event.target)
                  )
                "
                [disabled]="
                  !roleCatalogAvailable && !view.audienceRoleCodes?.includes(code)
                "
              />
              {{ roleLabel(code)
              }}<small *ngIf="roleCatalogAvailable && !roleCodes.includes(code)"
                >Unavailable</small
              >
            </label>
            <small *ngIf="!roleCatalogAvailable"
              >Load the role catalog to select roles.</small
            >
            <small *ngIf="roleCatalogAvailable && !visibleAudienceRoles.length"
              >No active roles are available.</small
            >
          </div>
          <small
            >Existing record access and action permissions still apply.</small
          >
        </fieldset>
        <section
          *ngIf="managed && workEditorAvailable && !reviewWorkView"
          class="dashboard-setting-section dashboard-settings-assignment"
          aria-label="Work assignment"
        >
          <h4>Work assignment</h4>
          <p>{{ responsibilitySummary || assignmentSetupSummary }}</p>
          <p class="dashboard-setting-hint">
            Assignment is configured in Work, separately from dashboard audience.
          </p>
          <button
            uiButton
            [text]="true"
            type="button"
            data-manage-dashboard-in-work
            [disabled]="loading"
            (click)="manageDashboardInWork(origin)"
          >
            Manage in Work <i class="pi pi-angle-right" aria-hidden="true"></i>
          </button>
        </section>
        <ng-container
          *ngIf="managed && !workEditorAvailable"
          [ngTemplateOutlet]="assignmentSettings"
        />
        <p
          *ngFor="let issue of queueSettingsIssues"
          class="dashboard-settings-issue"
          role="alert"
        >
          {{ issue }}
        </p>
      </div>
    </ng-container>
    <ng-container *ngIf="!dashboard" [ngTemplateOutlet]="assignmentSettings" />
    <p *ngIf="!dashboard" class="dashboard-setting-hint">
      Create a dashboard to choose its included work and audience.
    </p>
  </ng-template>
  <ng-template #assignmentSettings>
    <section
      class="dashboard-setting-section"
      aria-label="Step assignment"
      *ngIf="responsibilityEditor && !reviewWorkView"
    >
      <h4>Assignment</h4>
      <p class="dashboard-setting-hint">{{ assignmentSetupSummary }}</p>
      <ng-container *ngTemplateOutlet="responsibilityEditor" />
      <ng-container *ngIf="dashboard && assignmentSupported">
        <p
          class="dashboard-setting-hint"
          *ngIf="hasAssignmentColumn; else addAssignmentColumn"
        >
          The Assigned to dropdown shows the current owner and is blank when
          unassigned. Staff with assignment permission can select an eligible
          person or clear the selection. Changes save immediately.
        </p>
        <ng-template #addAssignmentColumn>
          <p class="dashboard-setting-hint">
            Add Assigned to so staff can see and change assignments from this
            dashboard.
          </p>
          <button
            uiButton
            type="button"
            (click)="addColumn('assigneeDisplayName')"
            [disabled]="dashboardControlsDisabled || !supportedDashboard"
          >
            Add Assigned to column
          </button>
        </ng-template>
      </ng-container>
    </section>
  </ng-template>
  <phms-workflow-detail-composer
    [guardPageSettings]="guardPageSettings"
    [guardContentLibrary]="guardContentLibrary || guardPageSettings"
    [guardNoticeSettings]="guardNoticeSettings || guardPageSettings"
    [latestDocument]="latestDocument"
    [ownerContext]="ownerContext"
    [formViewContext]="formViewContext"
    [pageSettingsView]="pageSettingsView"
    [pageSettingsViewState]="pageSettingsViewState"
    [active]="active"
    [workEditorAvailable]="workEditorAvailable"
    [focusBlock]="focusBlock"
    [focusForm]="focusForm"
    [focusAction]="focusAction"
    [componentCapabilities]="componentCapabilities"
    [componentCatalog]="componentCatalog"
    [resetVersion]="resetVersion"
    *ngIf="scope === 'detail' && editableRouting && document && node"
    [document]="document"
    [node]="node"
    [disabled]="disabled || loading"
    (pendingChangesChange)="onDetailPendingChanges($event)"
    (documentChange)="applyDetailDocument($event)"
    (openOutcomes)="openOutcomes.emit($event)"
    (openForm)="openForm.emit($event)"
    (openActions)="openActions.emit($event)"
    (noticeRequirementRequested)="onNoticeRequirementRequested($event)"
    (preview)="openPreview()"
  />

  <ng-template #previewTemplate>
    <section *ngIf="document && node && declaration" class="preview-dialog">
      <header>
        <div>
          <h3>Page preview</h3>
          <p>Synthetic examples · Unpublished settings · Submission disabled</p>
        </div>
        <button uiButton type="button" (click)="closePreview()">
          Close preview
        </button>
      </header>
      <phms-workflow-presentation-preview
        [componentCatalog]="componentCatalog"
        [document]="document"
        [node]="node"
        [scope]="scope === 'dashboard' ? 'dashboard' : 'detail'"
        [dashboardId]="dashboard?.id || ''"
      />
    </section>
  </ng-template>
</section>

<ng-template #dashboardSourceDetails>
  <p *ngIf="sourcePickerError" role="alert" tabindex="-1">{{ sourcePickerError }}</p>
</ng-template>
<ng-template #dashboardSourceReference let-entry>
  <details *ngIf="sourceChoice(entry.id) as source" class="dashboard-source-reference">
    <summary>Technical details</summary><pre>{{ source.capability | json }}</pre>
  </details>
</ng-template>
<phms-workflow-library-selector *ngIf="managedDashboardSources" title="Add dashboard"
  description="Choose what this dashboard shows. It opens on the canvas with starter columns and filters. You can change its name and settings afterward."
  searchLabel="Search dashboard types" allCategoryLabel="All dashboard types" [entries]="sourcePickerEntries"
  [open]="sourcePickerOpen" [active]="sourcePickerActive" [context]="sourcePickerContext" [loading]="loading"
  [statusTemplate]="dashboardSourceDetails" [entryDetailsTemplate]="dashboardSourceReference" emptyMessage="No dashboard types are available for this step."
  (chooseRequested)="chooseDashboardSource($event)" (closed)="dashboardSourcePickerClosed()" />

<ng-template #dashboardElementDetails>
  <p *ngIf="elementPickerError" role="alert" tabindex="-1">{{ elementPickerError }}</p>
  <section *ngIf="elementPickerChoice as choice" class="dashboard-source-details dashboard-element-details" data-dashboard-element-details aria-label="Selected dashboard element">
    <header><h4>{{ choice.label }}</h4><p>{{ choice.description }}</p></header>
    <p *ngIf="choice.required" class="dashboard-setting-hint">Required column. Keep this field when editing the dashboard.</p>
    <p *ngIf="choice.reason" role="status">{{ choice.reason }}</p>
    <ul *ngIf="choice.existing.length" class="dashboard-existing-elements" aria-label="Existing dashboard elements">
      <li *ngFor="let existing of choice.existing" [attr.data-existing-element]="existing.selection.kind + ':' + existing.selection.id">
        <span>{{ existing.label }}</span><small>{{ existing.selection.id }}</small>
        <button uiButton type="button" [attr.aria-label]="'Inspect existing ' + existing.label + ' (' + existing.selection.id + ')'"
          (click)="inspectExistingDashboardElement(existing.selection)">Inspect existing</button>
      </li>
    </ul>
    <button uiButton type="button" severity="primary" [outlined]="false" data-configure-dashboard-element [disabled]="!!choice.reason"
      (click)="configureDashboardElement(elementPickerContext)">Add &amp; configure</button>
  </section>
</ng-template>
<phms-workflow-library-selector *ngIf="managedDashboardElements" #dashboardElementLibrary title="Add dashboard element"
  description="Review one supported element before adding it to this dashboard. Browsing and closing leave the draft unchanged."
  searchLabel="Search dashboard elements" allCategoryLabel="All types" [categories]="elementCategories" [entries]="elementPickerEntries"
  [open]="elementPickerOpen" [active]="elementPickerActive" [context]="elementPickerContext" [statusTemplate]="dashboardElementDetails"
  emptyMessage="No supported dashboard elements are available."
  (opened)="dashboardElementPickerOpened()" (chooseRequested)="reviewDashboardElement($event)"
  (ownerActionRequested)="inspectDashboardElementChoice($event)" (closed)="dashboardElementPickerClosed()" />
